import type {
  SubscriptionData,
  SubscriptionMappingResponse
} from "src/components/Pegasus/types";
import { backendUrl } from "src/config/firebase";
import axios from "axios";

/**
 * Fetch subscription details from backend using purchase-id-token
 * @param purchaseIdToken - The purchase ID token
 * @returns The subscription data
 * @throws An error if the subscription data cannot be fetched
 */
export const fetchSubscription = async (
  purchaseIdToken: string
): Promise<SubscriptionData> => {
  try {
    const res = await axios.get(`${backendUrl}/org-subscription`, {
      headers: {
        "x-ms-marketplace-token": purchaseIdToken,
        "Content-Type": "application/json"
      },
      withCredentials: true
    });
    return res.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const message =
        (err.response?.data as { message?: string; error?: string })?.message ||
        (err.response?.data as { message?: string; error?: string })?.error ||
        err.message ||
        "Failed to fetch subscription";
      throw new Error(message);
    }
    throw new Error(
      err instanceof Error ? err.message : "Failed to fetch subscription"
    );
  }
};

/**
 * Create subscription mapping (onboard)
 * @param purchaseIdToken - The purchase ID token
 * @param subscriptionData - The subscription data
 * @returns The subscription mapping response
 * @throws An error if the subscription mapping cannot be created
 */
export const createSubscriptionMapping = async (
  purchaseIdToken: string,
  subscriptionData: SubscriptionData
): Promise<SubscriptionMappingResponse> => {
  try {
    const res = await axios.post(
      `${backendUrl}/org-subscription-mapping`,
      subscriptionData,
      {
        headers: {
          Authorization: `Bearer ${purchaseIdToken}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    return res.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const message =
        (err.response?.data as { message?: string; error?: string })?.message ||
        (err.response?.data as { message?: string; error?: string })?.error ||
        err.message ||
        "Failed to create subscription mapping";
      throw new Error(message);
    }

    throw new Error(
      err instanceof Error ? err.message : "Failed to create subscription mapping"
    );
  }
};
